import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';

import { isChunkLoadError, recoverFromChunkError } from '../../utils/chunkErrors';

// A failed lazy-route download is not an application crash: nothing was rendered
// and nothing was sent. Saying "the screen failed to render" next to a
// ChunkLoadError sent operators looking for a data problem instead of a dropped
// download, so the two are worded separately.
const CHUNK_TITLE = 'A part of this screen did not download';
const CHUNK_MESSAGE = 'The page code was interrupted in transit (a dropped connection or a stale cached copy). '
  + 'Nothing was saved or sent. Reload to fetch it again.';

/**
 * React Error Boundary component.
 * Catches JavaScript errors in its child component tree, logs them,
 * and displays a fallback UI instead of crashing the whole app.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      const chunkError = isChunkLoadError(this.state.error);
      // A caller-supplied fallback still wins for ordinary crashes, but a failed
      // chunk download always gets the reload affordance below: a bare fallback
      // leaves the operator with no way back.
      if (this.props.fallback && !chunkError) {
        return this.props.fallback;
      }

      return (
        <Box
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            maxWidth: 600,
            mx: 'auto',
            mt: 4
          }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              {chunkError ? CHUNK_TITLE : (this.props.title || 'Something went wrong')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {chunkError
                ? CHUNK_MESSAGE
                : (this.props.message
                  || 'An unexpected error occurred. The application has been isolated to prevent data loss.')}
            </Typography>
            {this.state.error && (
              <Box
                component="pre"
                sx={{
                  mt: 2,
                  p: 1.5,
                  fontSize: 11,
                  bgcolor: '#f5f5f5',
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 200,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {/* The message is shown in production too — a blank screen tells nobody
                    anything (that is exactly how a TDZ crash once looked). The component
                    stack stays dev-only. */}
                {this.state.error.toString()}
                {process.env.NODE_ENV === 'development' && this.state.errorInfo?.componentStack}
              </Box>
            )}
          </Alert>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* "Try Again" re-renders the same rejected `import()`; for a chunk
                error only a reload (after dropping the caches) can succeed. */}
            {!chunkError && (
              <Button variant="contained" onClick={this.handleReset}>
                Try Again
              </Button>
            )}
            <Button
              variant={chunkError ? 'contained' : 'outlined'}
              onClick={() => {
                if (typeof window === 'undefined') return;
                if (chunkError) {
                  // Drops a truncated cached body / a stale shell before reloading.
                  // The guard refuses a second automatic reload within a minute;
                  // an explicit click still reloads, just without the cache purge.
                  if (!recoverFromChunkError()) window.location.reload();
                  return;
                }
                window.location.reload();
              }}
            >
              Reload Page
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;